/**
 * **出牌"故障消失"的滤镜** —— 移植 Shadertoy 作品 "XtyXzW"
 * （<https://www.shadertoy.com/view/XtyXzW>，**CC BY-NC-SA 3.0**）里那层
 * 2D 故障，剥掉它原本的 3D raymarch 主体。
 *
 * 那一版的故障由四段合成（见原 shader 的 `glitchSwap / glitchTime / glitchStatic /
 * glitchColor`）：
 *
 * | 段 | 原做法 | 这里 |
 * | --- | --- | --- |
 * | **block swap** | 把画面按 group 切成子网格，随机**交换**两块（不是平移） | 原样移植（四档 group），这是它最标志性的一笔 |
 * | **static** | 随机块整体偏移几个单位（8-bit 抖动） | 移植，位移量按 2D 牌面收小 |
 * | **freeze time** | 某些块冻结动画时间 | 2D 无动画可冻，略去 |
 * | **color glitch** | 某些竖条块把颜色 ×10 或 ×0（黑/白闪烁） | 移植 |
 *
 * ⚠️ 原 shader 是 WebGL2/ES3，用了 `round()`；我们的程序编译成 **ES 1.00**
 *    （Pixi 把 `in` 换成 `varying`、`texture` 换成 `texture2D`），
 *    没有 `round`，所以这里用 `floor(x + .5)` 顶上。
 *
 * ⚠️ 输入贴图的物理尺寸在 `apply()` 里从 Pixi 现读（和 `bg/glitch.ts` 同一个理由：
 *    滤镜拿到的贴图可能比被渲染区域大一圈，用 uv 当屏幕坐标会整体错倍率）。
 */
import { Filter, UniformGroup } from 'pixi.js';
import { quadProgram } from '../filterQuad';

export const GLITCH_VANISH_FRAGMENT = /* glsl */ `
in vec2 vTextureCoord;
out vec4 finalColor;

uniform sampler2D uTexture;
/** (输入贴图宽, 高, amount 0..1, time) */
uniform vec4 uVA;

const float glitchScale = .5;

float _round(float n) { return floor(n + .5); }
vec2 _round2(vec2 n) { return floor(n + .5); }

float rand(vec2 co) {
  return fract(sin(dot(co.xy, vec2(12.9898, 78.233))) * 43758.5453);
}

vec2 glitchCoord(vec2 p, vec2 gridSize) {
  return floor(p / gridSize) * gridSize + gridSize / 2.;
}

float isInBlock(vec2 pos, vec4 block) {
  vec2 a = sign(pos - block.xy);
  vec2 b = sign(block.zw - pos);
  return min(sign(a.x + a.y + b.x + b.y - 3.), 0.);
}

vec2 moveDiff(vec2 pos, vec4 swapA, vec4 swapB) {
  return (swapB.xy - swapA.xy) * isInBlock(pos, swapA);
}

vec4 swapCoords(vec2 seed, vec2 groupSize, vec2 subGrid, vec2 blockSize) {
  vec2 rand2 = vec2(rand(seed), rand(seed + .1));
  vec2 range = subGrid - (blockSize - 1.);
  vec2 coord = floor(rand2 * range) / subGrid;
  vec2 bottomLeft = coord * groupSize;
  vec2 realBlockSize = (groupSize / subGrid) * blockSize;
  vec2 topRight = bottomLeft + realBlockSize;
  topRight -= groupSize / 2.;
  bottomLeft -= groupSize / 2.;
  return vec4(bottomLeft, topRight);
}

void swapBlocks(inout vec2 xy, vec2 groupSize, vec2 subGrid, vec2 blockSize, vec2 seed, float apply) {
  vec2 groupOffset = glitchCoord(xy, groupSize);
  vec2 pos = xy - groupOffset;
  vec2 seedA = seed * groupOffset;
  vec2 seedB = seed * (groupOffset + .1);
  vec4 swapA = swapCoords(seedA, groupSize, subGrid, blockSize);
  vec4 swapB = swapCoords(seedB, groupSize, subGrid, blockSize);
  vec2 newPos = pos;
  newPos += moveDiff(pos, swapA, swapB) * apply;
  newPos += moveDiff(pos, swapB, swapA) * apply;
  xy = newPos + groupOffset;
}

/** 每块一个稳定种子 + 随 amount 增长的出现概率 */
void glitchSeed(vec2 p, float amount, float time, out vec2 seed, out float prob) {
  float seedTime = floor(time * 8.);
  seed = vec2(1. + mod(seedTime / 100., 100.), 1. + mod(seedTime, 100.)) / 100.;
  seed += p;
  prob = amount * (0.4 + 0.6 * rand(glitchCoord(p, vec2(0.3))));
}

float shouldApply(vec2 seed, float prob) {
  return _round(mix(mix(rand(seed), 1., prob - .5), 0., (1. - prob) * .5));
}

void main() {
  vec2 uv = vTextureCoord;
  float amount = uVA.z;
  if (amount <= 0.001) {
    finalColor = texture(uTexture, uv);
    return;
  }
  float time = uVA.w;
  vec2 texels = max(uVA.xy, vec2(1.));
  float aspect = texels.x / texels.y;
  vec2 p = (uv - 0.5) * vec2(aspect, -1.) * 2.;

  vec2 seed;
  float prob;
  float apply;
  float apply2;

  // ---- glitchSwap（四档 group，原样）----
  {
    vec2 groupSize; vec2 subGrid; vec2 blockSize;
    groupSize = vec2(.6) * glitchScale; subGrid = vec2(2.); blockSize = vec2(1.);
    glitchSeed(glitchCoord(p, groupSize), amount, time, seed, prob);
    apply = shouldApply(seed, prob);
    swapBlocks(p, groupSize, subGrid, blockSize, seed, apply);

    groupSize = vec2(.8) * glitchScale; subGrid = vec2(3.); blockSize = vec2(1.);
    glitchSeed(glitchCoord(p, groupSize), amount, time, seed, prob);
    apply = shouldApply(seed, prob);
    swapBlocks(p, groupSize, subGrid, blockSize, seed, apply);

    groupSize = vec2(.2) * glitchScale; subGrid = vec2(6.); blockSize = vec2(1.);
    glitchSeed(glitchCoord(p, groupSize), amount, time, seed, prob);
    apply2 = shouldApply(seed, prob);
    swapBlocks(p, groupSize, subGrid, blockSize, seed + vec2(1.), apply * apply2);
    swapBlocks(p, groupSize, subGrid, blockSize, seed + vec2(2.), apply * apply2);
    swapBlocks(p, groupSize, subGrid, blockSize, seed + vec2(3.), apply * apply2);
    swapBlocks(p, groupSize, subGrid, blockSize, seed + vec2(4.), apply * apply2);
    swapBlocks(p, groupSize, subGrid, blockSize, seed + vec2(5.), apply * apply2);

    groupSize = vec2(1.2, .2) * glitchScale; subGrid = vec2(9., 2.); blockSize = vec2(3., 1.);
    glitchSeed(glitchCoord(p, groupSize), amount, time, seed, prob);
    apply = shouldApply(seed, prob);
    swapBlocks(p, groupSize, subGrid, blockSize, seed, apply);
  }

  // ---- glitchStatic（8-bit 抖动；位移量按 2D 牌面收小）----
  {
    vec2 groupSize = vec2(.5, .25 / 2.) * glitchScale;
    float grainSize = .2 * glitchScale;
    float contrast = .18;
    glitchSeed(glitchCoord(p, groupSize), amount * .5, time, seed, prob);
    if (shouldApply(seed, prob) == 1.) {
      vec2 seedB; float probB;
      glitchSeed(glitchCoord(p, vec2(grainSize)), 1., time, seedB, probB);
      vec2 offset = vec2(rand(seedB), rand(seedB + .1));
      offset = _round2(offset * 2. - 1.);
      p += offset * contrast;
    }
  }

  vec2 uv2 = p / vec2(aspect, -1.) * .5 + .5;
  if (uv2.x < 0. || uv2.x > 1. || uv2.y < 0. || uv2.y > 1.) {
    finalColor = texture(uTexture, uv);
    return;
  }
  vec4 color = texture(uTexture, uv2);

  // ---- glitchColor（竖条块颜色 ×10 / ×0）----
  {
    vec2 groupSize = vec2(.75, .125) * glitchScale;
    vec2 subGrid = vec2(0., 6.);
    glitchSeed(glitchCoord(p, groupSize), amount * .3, time, seed, prob);
    if (shouldApply(seed, prob) == 1.) {
      vec2 co = mod(p, groupSize) / groupSize;
      co *= subGrid;
      float a = max(co.x, co.y);
      color.rgb *= min(floor(mod(a, 2.)), 1.) * 10.;
    }
  }
  finalColor = color;
}
`;

export class GlitchVanishFilter extends Filter {
  readonly vanishUniforms: UniformGroup;

  constructor() {
    const uniforms = new UniformGroup({
      // (宽, 高, amount, time)：宽高在 `apply()` 里现读
      uVA: { value: new Float32Array([1, 1, 0, 0]), type: 'vec4<f32>' },
    });
    super({
      glProgram: quadProgram(GLITCH_VANISH_FRAGMENT, 'glitch-vanish'),
      resources: { vanishUniforms: uniforms },
      resolution: 'inherit',
    });
    this.vanishUniforms = uniforms;
  }

  /** amount 0..1 控制故障比例，time 换图案 */
  setFrame(amount: number, time: number): void {
    const a = this.vanishUniforms.uniforms.uVA as Float32Array;
    a[2] = Math.max(0, Math.min(1, amount));
    a[3] = time;
  }

  override apply(
    filterManager: Parameters<Filter['apply']>[0],
    input: Parameters<Filter['apply']>[1],
    output: Parameters<Filter['apply']>[2],
    clearMode: boolean,
  ): void {
    const a = this.vanishUniforms.uniforms.uVA as Float32Array;
    a[0] = input.source.pixelWidth;
    a[1] = input.source.pixelHeight;
    super.apply(filterManager, input, output, clearMode);
  }
}
